import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = 'https://groovie-business-api-production.up.railway.app/api/v1';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Interaction grid is 15-min (Fix 5b): a short segment no longer blocks its whole 30-min row —
// a second session fits beside it in the same half-hour. Rows are 15-min steps; labels stay at
// :00/:30 (readable) with faint :15/:45 tick lines.
const STEP_MIN = 15;                                    // interaction granularity (paint/click)
const TIME_SLOTS = [];
for (let h = 5; h <= 22; h++) {
  for (let m = 0; m < 60; m += STEP_MIN) {
    TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
  }
}

// Canvas geometry: blocks are positioned by real minutes, not quantized. Scale bumped to 1.6px/min
// (24px per 15-min cell) so the finer grid stays comfortably tap-able.
const SLOT_PX = 24;                                     // height of one 15-min interaction cell
const GRID_START_MIN = 5 * 60;                         // 05:00 = top of grid
const GRID_PX = TIME_SLOTS.length * SLOT_PX;           // full canvas height
const PX_PER_MIN = SLOT_PX / STEP_MIN;                  // 1.6px per minute
const MIN_BLOCK_PX = 22;                                // readable/tap-able floor for very short segments
const toMin = (t) => {
  if (!t || typeof t !== 'string') return null;
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
};

const CONTENT_TYPES = {
  music:        { label: 'Music',    bg: '#e9d5ff', border: '#a855f7', color: '#6b21a8' },
  news:         { label: 'News',     bg: '#bfdbfe', border: '#3b82f6', color: '#1e40af' },
  talk:         { label: 'Talk',     bg: '#fde68a', border: '#f59e0b', color: '#92400e' },
  announcement: { label: 'Announce', bg: '#bbf7d0', border: '#22c55e', color: '#166534' },
  jingle:       { label: 'Jingle',   bg: '#fbcfe8', border: '#ec4899', color: '#9d174d' },
  mixed:        { label: 'Mixed',    bg: '#99f6e4', border: '#14b8a6', color: '#115e59' }
};

const MOODS = ['energetic', 'motivational', 'uplifting', 'relaxed', 'peaceful', 'focused', 'romantic', 'neutral', 'fun', 'chill'];
const GENRES = ['pop', 'rock', 'jazz', 'classical', 'hip_hop', 'r_and_b', 'country', 'latin', 'reggae', 'gospel', 'afrobeats', 'lounge', 'workout', 'ambient', 'indie', 'soul'];

const Svg = ({ type, size = 16 }) => {
  const icons = {
    pin: <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></>,
    radio: <><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><circle cx="12" cy="12" r="2"/><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"/></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
    trash: <><polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></>,
    copy: <><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>,
    arrow: <><polyline points="9,18 15,12 9,6"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icons[type]}</svg>
  );
};

const inputStyle = {
  width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10,
  padding: '11px 14px', fontSize: 14, outline: 'none',
  fontFamily: "'Outfit', sans-serif", boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};
const focusIn = (e) => e.target.style.borderColor = '#7c3aed';
const focusOut = (e) => e.target.style.borderColor = '#e5e7eb';

const formatTime12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${m.toString().padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const nextSlot = (time) => {
  const [h, m] = time.split(':').map(Number);
  const t = h * 60 + m + STEP_MIN;
  return `${Math.floor(t / 60).toString().padStart(2, '0')}:${(t % 60).toString().padStart(2, '0')}`;
};

export default function RadioPrograms() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [programs, setPrograms] = useState([]);
  const [channels, setChannels] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(new Date().getDay() === 0 ? 6 : new Date().getDay() - 1);
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [activeBrush, setActiveBrush] = useState(null);
  const [isPainting, setIsPainting] = useState(false);
  const [paintedSlots, setPaintedSlots] = useState(new Set());
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', mood: 'neutral', genres: [], content_type: 'music', start_time: '09:00', end_time: '12:00' });
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  useEffect(() => { if (!token) { navigate('/login'); return; } fetchAll(); }, [token, navigate]);
  useEffect(() => { const chId = searchParams.get('channel'); if (chId) setSelectedChannel(chId); }, [searchParams]);

  const fetchAll = async () => { setLoading(true); await Promise.all([fetchPrograms(), fetchChannels(), fetchLocations()]); setLoading(false); };
  const fetchPrograms = async () => { try { const res = await fetch(`${API}/radio-programs`, { headers }); if (res.status === 401) { navigate('/login'); return; } if (res.ok) { const d = await res.json(); setPrograms(d.programs || []); } } catch { setPrograms([]); } };
  const fetchChannels = async () => { try { const res = await fetch(`${API}/channels`, { headers }); if (res.ok) { const d = await res.json(); setChannels(d.channels || []); } } catch {} };
  const fetchLocations = async () => { try { const res = await fetch(`${API}/locations`, { headers }); if (res.ok) { const d = await res.json(); setLocations(d.locations || []); } } catch {} };

  const getProgramAtSlot = useCallback((day, time) => {
    return programs.find(p => {
      if (p.day_of_week !== day) return false;
      if (selectedChannel !== 'all' && p.channel_id && p.channel_id !== parseInt(selectedChannel)) return false;
      return time >= p.start_time && time < p.end_time;
    });
  }, [programs, selectedChannel]);

  const handleSlotMouseDown = (day, time) => {
    const existing = getProgramAtSlot(day, time);
    if (existing) { openEditModal(existing); return; }
    if (!activeBrush) return;
    setIsPainting(true); setPaintedSlots(new Set([`${day}-${time}`]));
  };

  const handleSlotMouseEnter = (day, time) => {
    if (!isPainting || !activeBrush || day !== selectedDay) return;
    if (getProgramAtSlot(day, time)) return;
    setPaintedSlots(prev => new Set([...prev, `${day}-${time}`]));
  };

  const handleMouseUp = async () => {
    if (!isPainting || paintedSlots.size === 0) { setIsPainting(false); setPaintedSlots(new Set()); return; }
    const slots = [...paintedSlots].map(s => { const i = s.indexOf('-'); return { day: parseInt(s.substring(0, i)), time: s.substring(i + 1) }; }).sort((a, b) => a.time.localeCompare(b.time));
    const day = slots[0].day, startTime = slots[0].time, endTime = nextSlot(slots[slots.length - 1].time);
    const ct = (activeBrush === 'announcement' || activeBrush === 'jingle') ? 'mixed' : activeBrush;
    try {
      const res = await fetch(`${API}/radio-programs`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({
        name: `${CONTENT_TYPES[activeBrush]?.label || 'Program'} Block`, description: '', day_of_week: day,
        start_time: startTime, end_time: endTime, content_type: ct, genres: [], mood: 'neutral',
        channel_id: selectedChannel !== 'all' ? parseInt(selectedChannel) : null
      })});
      if (!res.ok) throw new Error('Failed');
      await fetchPrograms();
    } catch (err) { alert('Failed to create: ' + err.message); }
    setIsPainting(false); setPaintedSlots(new Set());
  };

  const openEditModal = (prog) => {
    setEditingProgram(prog);
    setEditForm({ name: prog.name || '', description: prog.description || '', mood: prog.mood || 'neutral', genres: prog.genres || [], content_type: prog.content_type || 'music', start_time: prog.start_time || '09:00', end_time: prog.end_time || '12:00' });
    setShowEditModal(true);
  };

  const handleEditSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const res = await fetch(`${API}/radio-programs/${editingProgram.id}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ name: editForm.name, description: editForm.description, day_of_week: editingProgram.day_of_week, start_time: editForm.start_time, end_time: editForm.end_time, content_type: editForm.content_type, genres: editForm.genres, mood: editForm.mood, channel_id: editingProgram.channel_id }) });
      if (!res.ok) throw new Error('Failed');
      await fetchPrograms(); setShowEditModal(false);
    } catch { alert('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!editingProgram || !window.confirm(`Delete "${editingProgram.name}"?`)) return;
    try { await fetch(`${API}/radio-programs/${editingProgram.id}`, { method: 'DELETE', headers }); await fetchPrograms(); setShowEditModal(false); } catch { alert('Failed'); }
  };

  const toggleGenre = (g) => setEditForm(prev => ({ ...prev, genres: prev.genres.includes(g) ? prev.genres.filter(x => x !== g) : [...prev.genres, g] }));

  const selectedChannelObj = channels.find(c => c.id === parseInt(selectedChannel));
  const selectedLocation = selectedChannelObj ? locations.find(l => l.id === selectedChannelObj.location_id) : null;
  const dayPrograms = programs.filter(p => p.day_of_week === selectedDay);
  const coveredSlots = TIME_SLOTS.filter(t => getProgramAtSlot(selectedDay, t));
  const coveragePercent = TIME_SLOTS.length > 0 ? Math.round((coveredSlots.length / TIME_SLOTS.length) * 100) : 0;

  // Lay out the day's programs as minute-proportional blocks; overlapping segments share columns.
  const visiblePrograms = programs.filter(p =>
    p.day_of_week === selectedDay &&
    (selectedChannel === 'all' || !p.channel_id || p.channel_id === parseInt(selectedChannel))
  );
  const laidOutPrograms = (() => {
    const items = visiblePrograms
      .map(p => { const s = toMin(p.start_time); let e = toMin(p.end_time); if (s == null) return null; if (e == null || e <= s) e = s + 5; return { p, s, e, col: 0, ncol: 1 }; })
      .filter(Boolean)
      .sort((a, b) => a.s - b.s || a.e - b.e);
    const clusters = []; let cur = []; let curEnd = -Infinity;
    for (const it of items) { if (cur.length && it.s >= curEnd) { clusters.push(cur); cur = []; curEnd = -Infinity; } cur.push(it); curEnd = Math.max(curEnd, it.e); }
    if (cur.length) clusters.push(cur);
    for (const cl of clusters) {
      const colEnd = [];
      for (const it of cl) { let c = 0; for (; c < colEnd.length; c++) { if (colEnd[c] <= it.s) break; } if (c === colEnd.length) colEnd.push(it.e); else colEnd[c] = it.e; it.col = c; }
      for (const it of cl) it.ncol = colEnd.length;
    }
    return items;
  })();

  if (loading) return (
    <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid #e9d5ff', borderTopColor: '#7c3aed', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1200, margin: '0 auto', fontFamily: "'Outfit', 'DM Sans', sans-serif" }}
      onMouseUp={handleMouseUp} onMouseLeave={() => { setIsPainting(false); setPaintedSlots(new Set()); }}>

      {/* Breadcrumb */}
      {selectedChannel !== 'all' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>
          {selectedLocation && (
            <>
              <button onClick={() => navigate('/locations')} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, padding: 0 }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#7c3aed'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>
                <Svg type="pin" size={12} /> {selectedLocation.name}
              </button>
              <Svg type="arrow" size={12} />
            </>
          )}
          {selectedChannelObj && (
            <>
              <button onClick={() => navigate('/channels')} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, padding: 0 }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#7c3aed'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>
                <Svg type="radio" size={12} /> {selectedChannelObj.name}
              </button>
              <Svg type="arrow" size={12} />
            </>
          )}
          <span style={{ fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Svg type="calendar" size={12} /> Schedule
          </span>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.5px' }}>Radio Schedule</h1>
          <p style={{ fontSize: 14, color: '#9ca3af', marginTop: 4 }}>{programs.length} programs &middot; {coveragePercent}% of {DAYS[selectedDay]} covered</p>
        </div>
        <select value={selectedChannel} onChange={(e) => setSelectedChannel(e.target.value)}
          style={{ ...inputStyle, width: 'auto', minWidth: 200 }}>
          <option value="all">All Channels</option>
          {channels.map(ch => {
            const loc = locations.find(l => l.id === ch.location_id);
            return <option key={ch.id} value={ch.id}>{loc ? `${loc.name} \u2192 ` : ''}{ch.name}</option>;
          })}
        </select>
      </div>

      {/* Brush Palette */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', padding: '14px 18px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginRight: 4 }}>Paint:</span>
          {Object.entries(CONTENT_TYPES).map(([key, info]) => (
            <button key={key} onClick={() => setActiveBrush(activeBrush === key ? null : key)}
              style={{
                padding: '8px 16px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                border: activeBrush === key ? `2px solid ${info.border}` : '1.5px solid #e5e7eb',
                background: activeBrush === key ? info.bg : '#fff',
                color: activeBrush === key ? info.color : '#6b7280',
                cursor: 'pointer', transition: 'all 0.15s',
                transform: activeBrush === key ? 'scale(1.05)' : 'none',
                boxShadow: activeBrush === key ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
              }}>{info.label}</button>
          ))}
          <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 8 }}>
            {activeBrush ? 'Click & drag on the grid to fill time blocks' : 'Select a content type, then paint the schedule'}
          </span>
        </div>
      </div>

      {/* Day Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, overflowX: 'auto' }}>
        {DAYS.map((day, idx) => {
          const count = programs.filter(p => p.day_of_week === idx).length;
          const isToday = idx === (new Date().getDay() === 0 ? 6 : new Date().getDay() - 1);
          return (
            <button key={idx} onClick={() => setSelectedDay(idx)}
              style={{
                position: 'relative', flexShrink: 0,
                padding: '8px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                background: selectedDay === idx ? '#7c3aed' : '#f3f4f6',
                color: selectedDay === idx ? '#fff' : '#6b7280',
              }}>
              {day}
              {count > 0 && (
                <span style={{
                  marginLeft: 6, fontSize: 11, padding: '1px 7px', borderRadius: 20,
                  background: selectedDay === idx ? 'rgba(255,255,255,0.25)' : '#e9d5ff',
                  color: selectedDay === idx ? '#fff' : '#7c3aed',
                }}>{count}</span>
              )}
              {isToday && (
                <span style={{ position: 'absolute', top: -3, right: -3, width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Schedule Grid */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 400, position: 'relative' }}>
            {/* Time-label + paint substrate: fixed 30-min rows (blocks are drawn as a minute-positioned overlay below) */}
            {TIME_SLOTS.map((time) => {
              const program = getProgramAtSlot(selectedDay, time);
              const isHour = time.endsWith(':00');
              const isHalf = time.endsWith(':30');
              const isPainted = paintedSlots.has(`${selectedDay}-${time}`);
              const brushInfo = activeBrush ? CONTENT_TYPES[activeBrush] : null;
              return (
                <div key={time} style={{
                  display: 'flex', height: SLOT_PX,
                  // :00 solid, :30 medium, :15/:45 faint tick lines
                  borderBottom: isHour ? '1px solid #e5e7eb' : (isHalf ? '1px solid #eef0f2' : '1px dashed #f4f5f7'),
                }}>
                  <div style={{
                    width: 80, flexShrink: 0, padding: '3px 12px 0', fontSize: 11, borderRight: '1px solid #e5e7eb',
                    fontWeight: isHour ? 600 : 400, color: isHour ? '#374151' : (isHalf ? '#9ca3af' : '#e5e7eb'),
                  }}>
                    {(isHour || isHalf) ? formatTime12(time) : ''}
                  </div>
                  <div
                    style={{
                      flex: 1, position: 'relative', userSelect: 'none', transition: 'background 0.1s',
                      background: isPainted && brushInfo ? brushInfo.bg : 'transparent',
                      opacity: isPainted ? 0.6 : 1,
                      cursor: activeBrush && !program ? 'crosshair' : program ? 'pointer' : 'default',
                    }}
                    onMouseDown={() => handleSlotMouseDown(selectedDay, time)}
                    onMouseEnter={() => handleSlotMouseEnter(selectedDay, time)}
                  >
                    {!program && !isPainted && isHour && activeBrush && (
                      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', padding: '0 12px' }}>
                        <span style={{ fontSize: 11, color: '#d1d5db' }}>Click to fill</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {/* Program blocks: absolutely positioned by real minutes \u2014 proportional height, true position, overlaps share columns */}
            <div style={{ position: 'absolute', top: 0, left: 80, right: 0, height: GRID_PX, pointerEvents: 'none' }}>
              {laidOutPrograms.map(({ p, s, e, col, ncol }) => {
                const typeInfo = CONTENT_TYPES[p.content_type] || CONTENT_TYPES.music;
                const top = Math.max(0, (s - GRID_START_MIN) * PX_PER_MIN);
                const height = Math.min(GRID_PX - top, Math.max(MIN_BLOCK_PX, (e - s) * PX_PER_MIN));
                const widthPct = 100 / (ncol || 1);
                const leftPct = (col || 0) * widthPct;
                const tight = height < 34;
                return (
                  <div key={p.id}
                    title={`${p.name}  \u00B7  ${formatTime12(p.start_time)} - ${formatTime12(p.end_time)}`}
                    onClick={(ev) => { ev.stopPropagation(); openEditModal(p); }}
                    style={{
                      position: 'absolute', top, height, left: `${leftPct}%`, width: `calc(${widthPct}% - 6px)`, marginLeft: 3,
                      borderRadius: 8, borderLeft: `4px solid ${typeInfo.border}`, background: typeInfo.bg,
                      padding: tight ? '2px 10px' : '6px 12px', display: 'flex', alignItems: tight ? 'center' : 'flex-start',
                      gap: 8, overflow: 'hidden', cursor: 'pointer', pointerEvents: 'auto', boxSizing: 'border-box',
                    }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: typeInfo.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                      {!tight && (
                        <div style={{ fontSize: 11, color: typeInfo.color, opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {formatTime12(p.start_time)} - {formatTime12(p.end_time)}
                          {p.mood && p.mood !== 'neutral' && ` \u00B7 ${p.mood}`}
                          {p.genres?.length > 0 && ` \u00B7 ${p.genres.join(', ')}`}
                        </div>
                      )}
                    </div>
                    {!tight && <span style={{ fontSize: 11, color: typeInfo.color, opacity: 0.5, flexShrink: 0 }}>{typeInfo.label}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Legend + Quick Actions */}
      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', fontSize: 12, color: '#6b7280' }}>
          {Object.entries(CONTENT_TYPES).map(([key, info]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: info.bg, border: `1px solid ${info.border}` }} />
              <span>{info.label}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button disabled={dayPrograms.length === 0} onClick={async () => {
            if (!window.confirm(`Copy ${DAYS[selectedDay]}'s schedule to the rest of the week (all other 6 days)? This replaces whatever is currently on those days.`)) return;
            const dayProgs = programs.filter(p => p.day_of_week === selectedDay);
            for (let d = 0; d < 7; d++) {
              if (d === selectedDay) continue;
              // Overwrite semantics (matches "Clear Day"): drop the target day's existing programs
              // first, so repeated copies don't stack duplicates (the backend POST only appends).
              for (const ex of programs.filter(p => p.day_of_week === d)) {
                try { await fetch(`${API}/radio-programs/${ex.id}`, { method: 'DELETE', headers }); } catch {}
              }
              for (const prog of dayProgs) {
                try { await fetch(`${API}/radio-programs`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ name: prog.name, description: prog.description, day_of_week: d, start_time: prog.start_time, end_time: prog.end_time, content_type: prog.content_type, genres: prog.genres || [], mood: prog.mood || 'neutral', channel_id: prog.channel_id, location_id: prog.location_id, content_sequence: prog.content_sequence || [] }) }); } catch {}
              }
            }
            await fetchPrograms(); alert('Copied to the whole week!');
          }} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            border: 'none', cursor: dayPrograms.length === 0 ? 'not-allowed' : 'pointer',
            background: '#f3f4f6', color: '#4b5563', opacity: dayPrograms.length === 0 ? 0.5 : 1,
          }}><Svg type="copy" size={12} /> Copy to Week</button>
          <button disabled={dayPrograms.length === 0} onClick={async () => {
            if (!window.confirm(`Clear all programs on ${DAYS[selectedDay]}?`)) return;
            for (const prog of dayPrograms) { try { await fetch(`${API}/radio-programs/${prog.id}`, { method: 'DELETE', headers }); } catch {} }
            await fetchPrograms();
          }} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            border: 'none', cursor: dayPrograms.length === 0 ? 'not-allowed' : 'pointer',
            background: '#fef2f2', color: '#dc2626', opacity: dayPrograms.length === 0 ? 0.5 : 1,
          }}><Svg type="trash" size={12} /> Clear Day</button>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingProgram && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', padding: 32, boxShadow: '0 25px 60px rgba(0,0,0,0.15)' }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 24px' }}>Edit Program</h2>
            <form onSubmit={handleEditSave}>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Name</label>
                <input type="text" required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Description</label>
                <input type="text" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  style={inputStyle} onFocus={focusIn} onBlur={focusOut} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Start</label>
                  <input type="time" value={editForm.start_time} onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>End</label>
                  <input type="time" value={editForm.end_time} onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })} style={inputStyle} />
                </div>
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Content Type</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {Object.entries(CONTENT_TYPES).map(([key, info]) => (
                    <button key={key} type="button" onClick={() => setEditForm({ ...editForm, content_type: key })}
                      style={{
                        padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                        border: editForm.content_type === key ? `2px solid ${info.border}` : '1.5px solid #e5e7eb',
                        background: editForm.content_type === key ? info.bg : '#fff',
                        color: editForm.content_type === key ? info.color : '#6b7280',
                        cursor: 'pointer',
                      }}>{info.label}</button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Mood</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {MOODS.map(m => (
                    <button key={m} type="button" onClick={() => setEditForm({ ...editForm, mood: m })}
                      style={{
                        padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        border: 'none', cursor: 'pointer', textTransform: 'capitalize',
                        background: editForm.mood === m ? '#7c3aed' : '#f3f4f6',
                        color: editForm.mood === m ? '#fff' : '#6b7280',
                      }}>{m}</button>
                  ))}
                </div>
              </div>
              {(editForm.content_type === 'music' || editForm.content_type === 'mixed') && (
                <div style={{ marginBottom: 18 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Genres</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {GENRES.map(g => (
                      <button key={g} type="button" onClick={() => toggleGenre(g)}
                        style={{
                          padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          border: 'none', cursor: 'pointer', textTransform: 'capitalize',
                          background: editForm.genres.includes(g) ? '#7c3aed' : '#f3f4f6',
                          color: editForm.genres.includes(g) ? '#fff' : '#6b7280',
                        }}>{g.replace('_', ' ')}</button>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderTop: '1px solid #f3f4f6', paddingTop: 20 }}>
                <button type="button" onClick={handleDelete} style={{
                  padding: '10px 16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                  background: 'transparent', color: '#dc2626', fontSize: 13, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                ><Svg type="trash" size={14} /> Delete</button>
                <div style={{ flex: 1 }} />
                <button type="button" onClick={() => setShowEditModal(false)} style={{
                  padding: '12px 24px', borderRadius: 10, border: '1.5px solid #e5e7eb',
                  background: '#fff', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif",
                }}>Cancel</button>
                <button type="submit" disabled={saving} style={{
                  padding: '12px 24px', borderRadius: 10, border: 'none',
                  background: saving ? '#c4b5fd' : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                  color: '#fff', fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                  fontFamily: "'Outfit', sans-serif", boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
                }}>{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}